import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { PwaProvider, usePwa } from "./context/PwaContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ChatProvider } from "./context/ChatContext";
import { OrdersProvider } from "./context/OrdersContext";
import { CallProvider } from "./context/CallContext";
import { BottomNav } from "./components/BottomNav";
import { InstallBanner } from "./components/InstallBanner";
import { PushOnboardingBanner } from "./components/PushOnboardingBanner";
import { CallOverlay } from "./components/CallOverlay";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import UserProfile from "./pages/UserProfile";
import Admin from "./pages/Admin";
import Chats from "./pages/Chats";
import ChatView from "./pages/ChatView";
import Orders from "./pages/Orders";
import Calls from "./pages/Calls";
import Contacts from "./pages/Contacts";

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.roles.includes("admin")) return <Navigate to="/profile" replace />;
  return children;
}

function Layout() {
  const { bannerHeight } = usePwa();
  const { user } = useAuth();
  const location = useLocation();
  const hideNav = ["/login", "/register"].includes(location.pathname) || /^\/chats\/.+/.test(location.pathname);
  return (
    <div className="max-w-[480px] mx-auto min-h-screen bg-white relative shadow-sm" style={{ "--push-banner-height": `${bannerHeight}px` }}>
      {user && <PushOnboardingBanner key={user.id} />}
      <Routes>
        <Route path="/" element={<Navigate to="/orders" replace />} />
        <Route path="/login" element={user ? <Navigate to="/orders" replace /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to="/orders" replace /> : <Register />} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/users/:id" element={<Protected><UserProfile /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
        <Route path="/chats" element={<Protected><Chats /></Protected>} />
        <Route path="/calls" element={<Protected><Calls /></Protected>} />
        <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
        <Route path="/chats/u/:userId" element={<Protected><ChatView key={location.pathname} /></Protected>} />
        <Route path="/chats/:chatId" element={<Protected><ChatView key={location.pathname} /></Protected>} />
        <Route path="/orders" element={<Protected><Orders /></Protected>} />
        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Routes>
      {user && !hideNav && (
        <>
          <BottomNav />
          <InstallBanner />
        </>
      )}
      {user && <CallOverlay />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ChatProvider>
      <PwaProvider>
        <OrdersProvider>
          <CallProvider>
            <BrowserRouter>
              <Layout />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </CallProvider>
        </OrdersProvider>
      </PwaProvider>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;
